import Big from 'big.js';
import { Formik } from 'formik';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import {
  BillableIcon,
  Button,
  Buttons,
  CancelButton,
  Currency,
  DayPickerInput,
  Drawer,
  Field,
  FiltersBar,
  Form,
  Icon,
  SingleSelectFilter,
  Tab,
  Table,
  Tabs,
  TextInput,
  Tooltip,
} from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useAuth, useDirtyCheck } from '~/hooks';
import { colors } from '~/styles';
import { dateFormats, emptyStringToNull, mergeValues } from '~/utils';
import RevalueConfirmation from './RevalueConfirmation';
import { coveringCard, isFirstCard } from './timeline';

// A new card starts from the rates of the card in effect on its start date; it runs until the next card starts.

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-top: 1.625rem;
  margin-bottom: 1.625rem;
`;

const Filters = styled.div`
  margin-bottom: 1rem;
`;

const Name = styled.span`
  color: ${({ $inactive }) => ($inactive ? colors.grey40 : 'inherit')};
`;

export default function RateCardForm({ project, rateCards, rateCardId, readOnly = false, onSaved, onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();
  const auth = useAuth();
  const confirmation = useConfirmation();

  const [rateCard, setRateCard] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [statusId, setStatusId] = useState('active');

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!rateCardId) {
        if (!cancelled) setRateCard({});
        return;
      }
      try {
        const { data } = await api.www.workspaces(workspace.id).projectSettings(project.id).rateCards(rateCardId).get();
        if (!cancelled) setRateCard(data);
      } catch (error) {
        if (!cancelled) toast.error(error.message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [api, workspace.id, project.id, rateCardId, toast]);

  const formRef = useRef();
  const seededFrom = useRef(null);
  const dirtyCheck = useDirtyCheck(() => formRef.current?.dirty);

  // Cost overrides follow the same gate as the member cost rate modal: anyone
  // who can see margin sees them, only a workspace admin changes them.
  const canEditCost = auth.workspace.manage;
  const showCost = canEditCost || project.permissions.viewMargin;
  const showMembers = !project.useRoles || showCost;

  const today = moment().format(dateFormats.isoDate);

  const handleSubmit = async (values, formik) => {
    const endpoint = api.www.workspaces(workspace.id).projectSettings(project.id).rateCards;
    const body = emptyStringToNull({ notes: values.notes });
    if (!rateCardId || !isFirstCard(rateCard)) body.start = values.start;
    if (showMembers) body.memberRates = values.memberRates.map(toMemberRateBody);
    if (project.useRoles) body.roleRates = values.roleRates.map(toRoleRateBody);

    // A new card revalues the time already tracked inside the window it takes over from the card it splits.
    const split = coveringCard(rateCards, values.start);
    const revalues = rateCardId
      ? revaluesTrackedTime(values, rateCard) || movesTrackedTime(rateCards, rateCard, body.start)
      : split?.lastTrackedOn >= values.start && ratesChanged(values, split);

    if (revalues) {
      const cardWindow = rateCardId
        ? { start: rateCard.start, end: rateCard.end }
        : derivedWindow(rateCards, rateCardId, values.start);

      const confirmed = await confirmation.prompt((resolve) => (
        <RevalueConfirmation rateCards={[cardWindow]} resolve={resolve} />
      ));
      if (!confirmed) return;
    }

    formik.setSubmitting(true);

    try {
      if (rateCardId) {
        await endpoint(rateCardId).patch(body);
      } else {
        await endpoint().post(body);
      }

      toast.success(`Rate card ${rateCardId ? 'updated' : 'created'} successfully.`);
      await onSaved();
    } catch (error) {
      toast.error(error.message);
    }

    formik.setSubmitting(false);
  };

  if (!rateCard) return null;

  const isFirst = rateCardId ? isFirstCard(rateCard) : false;

  const source = rateCardId ? rateCard : coveringCard(rateCards, today);

  const initialValues = mergeValues(
    { notes: '', start: today, memberRates: [], roleRates: [] },
    {
      notes: rateCard.notes ?? '',
      start: rateCard.start ?? today,
      memberRates: toMemberRows(source),
      roleRates: toRoleRows(source),
    },
  );

  return (
    <Drawer
      title={
        readOnly
          ? `View Rate Card #${rateCard.number}`
          : rateCardId
            ? `Edit Rate Card #${rateCard.number}`
            : 'New Rate Card'
      }
      width={showCost ? '64rem' : '54rem'}
      isOpen
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => (
        <Formik
          innerRef={formRef}
          enableReinitialize
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validateOnBlur={false}
          validateOnChange={false}
          validationSchema={Yup.object().shape({
            notes: Yup.string().label('Notes').max(5000).nullable(),
            start: Yup.date().label('Starts On').required(),
          })}>
          {(formik) => {
            // While the seeded rates are untouched, changing the date re-seeds them from the card covering it.
            const handleStartChange = (start) => {
              if (start === undefined) return;
              // Typing passes null for each partial date, so the seed card can't be derived from the current start.
              const previous = seededFrom.current ?? source;
              const next = coveringCard(rateCards, start);
              formik.setFieldValue('start', start);

              if (rateCardId || !next || next === previous) return;

              const untouched =
                _.isEqual(formik.values.memberRates, toMemberRows(previous)) &&
                _.isEqual(formik.values.roleRates, toRoleRows(previous));
              if (untouched) {
                seededFrom.current = next;
                formik.setFieldValue('memberRates', toMemberRows(next));
                formik.setFieldValue('roleRates', toRoleRows(next));
              }
            };

            const cardWindow = isFirst ? rateCard : derivedWindow(rateCards, rateCardId, formik.values.start);
            const hasStart = isFirst || moment(formik.values.start).isValid();

            const statusFilter = (
              <Filters>
                <FiltersBar>
                  <SingleSelectFilter
                    placeholder="Status"
                    options={[
                      { id: 'active', name: 'Active' },
                      { id: 'inactive', name: 'Inactive' },
                    ]}
                    value={statusId}
                    onChange={({ value }) => setStatusId(value)}
                  />
                </FiltersBar>
              </Filters>
            );

            const details = (
              <>
                <Form.Section title="Effective Dates">
                  <Form.Control
                    help={!rateCardId ? 'Rates are pre-filled from the card in effect on the start date.' : undefined}>
                    {isFirst ? (
                      <TextInput placeholder="Starts On" value="Project start" disabled />
                    ) : (
                      <Field.DayPicker
                        name="start"
                        placeholder="Starts On"
                        clearable={false}
                        disabled={readOnly}
                        onChange={handleStartChange}
                      />
                    )}
                    {hasStart && !cardWindow.end ? (
                      <TextInput placeholder="Ends On" value="Ongoing" disabled />
                    ) : (
                      <DayPickerInput
                        placeholder="Ends On"
                        value={hasStart ? cardWindow.end : null}
                        clearable={false}
                        disabled
                      />
                    )}
                  </Form.Control>
                </Form.Section>

                <Form.Section title="Notes">
                  <Form.Control>
                    <Field.TextArea name="notes" placeholder="Notes" rows={2} maxLength={5000} disabled={readOnly} />
                  </Form.Control>
                </Form.Section>
              </>
            );

            const roleRates = (
              <>
                {statusFilter}

                <Table small>
                  <Table.Header>
                    <Table.Column>Role</Table.Column>
                    <Table.Column width="12rem" align="right">
                      Bill Rate
                    </Table.Column>
                  </Table.Header>

                  <Table.Body>
                    {formik.values.roleRates.length === 0 && (
                      <Table.Row>
                        <Table.Cell>This project has no roles yet.</Table.Cell>
                      </Table.Row>
                    )}

                    {formik.values.roleRates.map(
                      (row, index) =>
                        isShown(row, statusId) && (
                          <Table.Row key={row.projectRoleId}>
                            <Table.Cell>
                              <Name $inactive={!row.isActive}>{row.name}</Name>
                              <TrackedTimeIcon row={row} />
                            </Table.Cell>
                            <Table.Cell>
                              <BillableIcon value={row.isBillable} />
                              {row.isBillable && (
                                <Field.Currency
                                  name={`roleRates.${index}.rate`}
                                  currency={project.currency}
                                  disabled={readOnly}
                                  precision={7}
                                />
                              )}
                            </Table.Cell>
                          </Table.Row>
                        ),
                    )}
                  </Table.Body>
                </Table>
              </>
            );

            const memberRates = (
              <>
                {statusFilter}

                <Table small>
                  <Table.Header>
                    <Table.Column>Member</Table.Column>
                    <Table.Column width="12rem" align="right" isVisible={tabIndex === 1}>
                      Bill Rate
                    </Table.Column>
                    <Table.Column width="10rem" align="center" isVisible={tabIndex === 2}>
                      Override Cost
                    </Table.Column>
                    <Table.Column width="12rem" align="right" isVisible={tabIndex === 2}>
                      Cost/Hour
                    </Table.Column>
                    <Table.Column width="12rem" align="right" isVisible={tabIndex === 2}>
                      Overhead/Hour
                    </Table.Column>
                    <Table.Column width="10rem" align="right" isVisible={tabIndex === 2}>
                      Total/Hour
                    </Table.Column>
                  </Table.Header>

                  <Table.Body>
                    {formik.values.memberRates.length === 0 && (
                      <Table.Row>
                        <Table.Cell>This project has no members yet.</Table.Cell>
                      </Table.Row>
                    )}

                    {formik.values.memberRates.map(
                      (row, index) =>
                        isShown(row, statusId) && (
                          <Table.Row key={row.memberId}>
                            <Table.Cell>
                              <Name $inactive={!row.isActive}>{row.name}</Name>
                              <TrackedTimeIcon row={row} />
                            </Table.Cell>
                            <Table.Cell>
                              <BillableIcon value={row.isBillable} />
                              {row.isBillable && (
                                <Field.Currency
                                  name={`memberRates.${index}.rate`}
                                  currency={project.currency}
                                  disabled={readOnly}
                                  precision={7}
                                />
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              <Field.Checkbox
                                name={`memberRates.${index}.overrideCostRate`}
                                disabled={readOnly || !canEditCost}
                              />
                            </Table.Cell>
                            <Table.Cell>
                              <Field.Currency
                                name={`memberRates.${index}.costPerHour`}
                                currency={row.costCurrency || project.currency}
                                disabled={readOnly || !canEditCost || !row.overrideCostRate}
                                precision={7}
                              />
                            </Table.Cell>
                            <Table.Cell>
                              <Field.Currency
                                name={`memberRates.${index}.overheadCostPerHour`}
                                currency={row.costCurrency || project.currency}
                                disabled={readOnly || !canEditCost || !row.overrideCostRate}
                                precision={7}
                              />
                            </Table.Cell>
                            <Table.Cell data-testid="total_cost_per_hour">
                              <Currency
                                value={
                                  row.overrideCostRate &&
                                  (_.isNumber(row.costPerHour) || _.isNumber(row.overheadCostPerHour))
                                    ? Big(row.costPerHour || 0)
                                        .plus(row.overheadCostPerHour || 0)
                                        .toNumber()
                                    : null
                                }
                                currency={row.costCurrency || project.currency}
                                maximumFractionDigits={7}
                              />
                            </Table.Cell>
                          </Table.Row>
                        ),
                    )}
                  </Table.Body>
                </Table>
              </>
            );

            return (
              <Form>
                <Tabs selectedIndex={tabIndex} onChange={(index) => setTabIndex(index)}>
                  <Tab>
                    Details
                    {(formik.errors.start || formik.errors.notes) && (
                      <Icon icon="exclamation-circle" color={colors.danger} spaceLeft />
                    )}
                  </Tab>
                  {project.isBillable && <Tab>Bill Rates</Tab>}
                  {showCost && <Tab>Cost Rates</Tab>}
                </Tabs>

                <Content>{[details, project.useRoles ? roleRates : memberRates, memberRates][tabIndex]}</Content>
                <Drawer.Actions>
                  <Buttons align="right">
                    <CancelButton onClick={() => dirtyCheck(() => closeDrawer())}>Close</CancelButton>
                    {!readOnly && (
                      <Button isLoading={formik.isSubmitting} onClick={formik.submitForm}>
                        Save &amp; Close
                      </Button>
                    )}
                  </Buttons>
                </Drawer.Actions>
              </Form>
            );
          }}
        </Formik>
      )}
    </Drawer>
  );
}

function TrackedTimeIcon({ row }) {
  if (!row.hasTimeEntries) return null;

  return (
    <Tooltip Container="span" message="Time has been tracked in this period">
      <Icon icon="clock" color={colors.grey40} spaceLeft data-testid="tracked_time" />
    </Tooltip>
  );
}

function revaluesTrackedTime(values, rateCard) {
  const changed = (rows, initial) => rows.some((row, index) => row.hasTimeEntries && !_.isEqual(row, initial[index]));

  return changed(values.memberRates, toMemberRows(rateCard)) || changed(values.roleRates, toRoleRows(rateCard));
}

function movesTrackedTime(rateCards, rateCard, start) {
  if (start > rateCard.start)
    return rateCards.some((card) => card.start >= rateCard.start && card.start < start && card.firstTrackedOn < start);
  if (start < rateCard.start)
    return rateCards.some((card) => (card.start ?? '') < rateCard.start && card.lastTrackedOn >= start);
  return false;
}

function ratesChanged(values, rateCard) {
  const changed = (rows, initial) => rows.some((row, index) => !_.isEqual(row, initial[index]));

  return changed(values.memberRates, toMemberRows(rateCard)) || changed(values.roleRates, toRoleRows(rateCard));
}

function derivedWindow(rateCards, rateCardId, start) {
  const nextCard = _.minBy(
    rateCards.filter((row) => row.id !== rateCardId && row.start > start),
    'start',
  );

  return { start, end: nextCard ? moment(nextCard.start).subtract(1, 'day').format(dateFormats.isoDate) : null };
}

function isShown(row, statusId) {
  return !statusId || (statusId === 'active') === row.isActive;
}

function toMemberRows(rateCard) {
  return _.sortBy(rateCard?.memberRates ?? [], (row) => row.member?.name).map((row) => ({
    memberId: row.memberId,
    name: row.member?.name ?? '—',
    isActive: (row.member?.isActive ?? true) && (row.projectMember?.isActive ?? true),
    isBillable: row.projectMember?.isBillable ?? true,
    hasTimeEntries: row.hasTimeEntries ?? false,
    rate: row.rate ?? '',
    overrideCostRate: row.overrideCostRate ?? false,
    costCurrency: row.costCurrency || '',
    costPerHour: row.costPerHour ?? '',
    overheadCostPerHour: row.overheadCostPerHour ?? '',
  }));
}

function toRoleRows(rateCard) {
  return _.sortBy(rateCard?.roleRates ?? [], (row) => row.projectRole?.name).map((row) => ({
    projectRoleId: row.projectRoleId,
    name: row.projectRole?.name ?? '—',
    isActive: row.projectRole?.isActive ?? true,
    isBillable: row.projectRole?.isBillable ?? true,
    hasTimeEntries: row.hasTimeEntries ?? false,
    rate: row.rate ?? '',
  }));
}

function toMemberRateBody(row) {
  return emptyStringToNull({
    memberId: row.memberId,
    rate: row.rate,
    overrideCostRate: Boolean(row.overrideCostRate),
    costPerHour: row.costPerHour,
    overheadCostPerHour: row.overheadCostPerHour,
  });
}

function toRoleRateBody(row) {
  return emptyStringToNull({ projectRoleId: row.projectRoleId, rate: row.rate });
}
